import { cloneDeep, compact, concat, isEmpty, last, orderBy } from 'lodash';
import { useState } from 'react';
import { BACKUP_ASSEMBLY_COMPONENT_CONFIG } from 'src/constants';
import { useModal } from 'src/hooks';
import { ComponentTypes, IComponent } from 'src/interfaces';
import { getBackgroundVideoDuration } from 'src/utils/file-types';
import { uid } from 'uid';

import {
  IDefaultSection,
  IGridSection,
  IScheduledSectionBackupComponent,
  IScheduledSectionComponent,
} from '../../../ScheduleComponentPage.interface';

export const useManageBackupComponent = (
  selectedSection: IGridSection | IDefaultSection,
  onUpdateScreensGridSectionsWhenSectionChanges: (
    section: IGridSection | IDefaultSection,
  ) => void,
  duration?: number,
) => {
  const [filterParameters, setFilterParameters] = useState<string[]>([]);
  const [mainComponent, setMainComponent] =
    useState<IScheduledSectionComponent>();
  const [selectedPreviewComponent, setSelectedPreviewComponent] =
    useState<IScheduledSectionBackupComponent>();

  const backupComponentListModal = useModal();
  const backupComponentPreviewModal = useModal();

  const onAddNewBackup = (mainComponent: IScheduledSectionComponent) => {
    const filteredComponentIds: number[] = concat(
      compact(mainComponent.backups?.map((item) => item.component?.id ?? 0)),
      mainComponent.component?.id ?? 0,
    );

    setMainComponent(mainComponent);
    setFilterParameters([
      `component_type_id=${mainComponent.component?.component_type_id}`,
      ...(mainComponent.scheduleEndDate
        ? [`end_date.from=${mainComponent.scheduleEndDate}`]
        : []),
      `id.notlist=${filteredComponentIds.join('|')}`,
      `height=${mainComponent.component?.height}`,
      `width=${mainComponent.component?.width}`,
      `layout_type_id=${mainComponent.component?.layout_type_id}`,
    ]);

    backupComponentListModal.onOpenModal();
  };

  const onCloseBackupComponentListModal = () => {
    backupComponentListModal.onCloseModal();
  };

  const onSelectBackup = async (backupComponent: IComponent) => {
    let updatedDuration =
      duration ||
      mainComponent?.config?.duration ||
      BACKUP_ASSEMBLY_COMPONENT_CONFIG.duration;
    if (
      backupComponent?.component_type?.name === ComponentTypes.PROMOTIONAL_SPACE
    ) {
      updatedDuration = await getBackgroundVideoDuration(
        backupComponent?.config?.background,
        updatedDuration,
        1000,
      );
    }

    const newComponents = (
      selectedSection.components as IScheduledSectionComponent[]
    )?.map((item) => {
      if (item.uid !== mainComponent?.uid) {
        return item;
      }

      const { backups = [] } = item;
      const ascendingBackupComponents = orderBy(backups, 'backup_order', 'asc');
      const newBackup = {
        uid: uid(),
        id: 0,
        assembly_id: -1,
        component_id: backupComponent.id,
        rotation_order: -1,
        backup_order: last(ascendingBackupComponents)
          ? (
              last(
                ascendingBackupComponents,
              ) as IScheduledSectionBackupComponent
            ).backup_order + 1
          : 1,
        config: {
          transition: 'fade',
          duration: updatedDuration,
        },
        component: backupComponent,
      };

      return {
        ...item,
        backups: [...backups, newBackup],
      };
    });

    onUpdateScreensGridSectionsWhenSectionChanges({
      ...selectedSection,
      components: newComponents,
    });

    backupComponentListModal.onCloseModal();
  };

  const onDeleteBackup = (
    selectedMainComponent: IScheduledSectionComponent,
    backupComponent: IScheduledSectionBackupComponent,
  ) => {
    const newComponents = (
      selectedSection.components as IScheduledSectionComponent[]
    )?.map((item) => {
      if (item.uid !== selectedMainComponent?.uid) {
        return item;
      }

      const newBackups = (item.backups as IScheduledSectionBackupComponent[])
        .filter((item) => item.uid !== backupComponent.uid)
        .map((item, index) => ({
          ...item,
          backup_order: index + 1,
        }));

      return {
        ...item,
        backups: newBackups,
      };
    });

    onUpdateScreensGridSectionsWhenSectionChanges({
      ...selectedSection,
      components: newComponents,
    });
  };

  const onReorderBackup = (
    selectedMainComponent: IScheduledSectionComponent,
    oldIndex: number,
    newIndex: number,
  ) => {
    const newComponents = (
      selectedSection.components as IScheduledSectionComponent[]
    )?.map((item) => {
      if (item.uid !== selectedMainComponent?.uid) {
        return item;
      }

      const newBackups = cloneDeep(
        item.backups,
      ) as IScheduledSectionBackupComponent[];
      const movedComponent = newBackups[oldIndex];

      newBackups.splice(oldIndex, 1);
      newBackups.splice(newIndex, 0, movedComponent);
      newBackups.forEach((item, index) => {
        item.backup_order = index + 1;
      });

      return {
        ...item,
        backups: newBackups,
      };
    });

    onUpdateScreensGridSectionsWhenSectionChanges({
      ...selectedSection,
      components: newComponents,
    });
  };

  const onPreviewBackup = (
    backupComponent: IScheduledSectionBackupComponent,
  ) => {
    setSelectedPreviewComponent(backupComponent);
    backupComponentPreviewModal.onOpenModal();
  };

  return {
    openBackupComponentListModal:
      backupComponentListModal.open && !isEmpty(filterParameters),
    backupComponentPreviewModal,
    filterParameters,
    selectedPreviewComponent,
    onAddNewBackup,
    onCloseBackupComponentListModal,
    onSelectBackup,
    onDeleteBackup,
    onReorderBackup,
    onPreviewBackup,
  };
};
